import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, X } from "lucide-react";
import api from "@/lib/api";

export default function QuizPlay() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [quiz, setQuiz] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [order, setOrder] = useState([]);
    const [answers, setAnswers] = useState([]);
    const [submitted, setSubmitted] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = () => api.get(`/quizzes/${id}`).then(({ data }) => {
        setQuiz(data.quiz);
        let qs = data.questions;
        let idx = qs.map((_, k) => k);
        if (data.quiz?.randomize) {
            for (let j = idx.length - 1; j > 0; j--) {
                const k = Math.floor(Math.random() * (j + 1)); [idx[j], idx[k]] = [idx[k], idx[j]];
            }
        }
        setOrder(idx);
        setQuestions(qs);
        setAnswers(new Array(qs.length).fill(-1));
        setSubmitted(null);
        window.scrollTo({ top: 0, behavior: "smooth" });
    });

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const setAnswer = (qi, ai) => {
        const next = [...answers]; next[qi] = ai; setAnswers(next);
    };

    const submit = async () => {
        setBusy(true);
        try {
            // submit in displayed order (questions already in display order via `order`)
            const orderedAnswers = order.map((origIdx) => answers[order.indexOf(origIdx)]);
            // Simpler: the answers array indices follow original questions order returned by backend.
            // We display by `order` but track by display index in `answers`. Map back to backend order:
            const answersByBackend = new Array(questions.length).fill(0);
            order.forEach((origIdx, displayIdx) => {
                answersByBackend[origIdx] = answers[displayIdx] ?? 0;
            });
            const { data } = await api.post("/quiz-attempts", { quiz_id: id, answers: answersByBackend });
            setSubmitted(data);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } finally { setBusy(false); }
    };

    if (!quiz) return <div className="text-sm text-ink-500" data-testid="quiz-loading">Loading…</div>;

    const allAnswered = answers.every((a) => a >= 0);

    return (
        <div className="max-w-3xl mx-auto space-y-8" data-testid="quiz-play-page">
            <div>
                <Link to="/quizzes" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.15em] text-ink-500 hover:text-ink-900" data-testid="quiz-back-link">
                    <ArrowLeft size={12} /> Quizzes
                </Link>
                <div className="mt-4">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono">Assessment</div>
                    <h1 className="font-editorial text-3xl md:text-4xl text-ink-900 mt-2">{quiz.title}</h1>
                    {quiz.description && <p className="mt-2 text-sm text-ink-500">{quiz.description}</p>}
                </div>
            </div>

            {submitted ? (
                <Result quiz={quiz} questions={questions} order={order} submitted={submitted} retake={load} navigate={navigate} />
            ) : (
                <div className="space-y-6">
                    {order.map((origIdx, displayIdx) => {
                        const q = questions[origIdx];
                        return (
                            <div key={q.id} className="bg-white border border-ink-300 p-6 md:p-8" data-testid={`question-${q.id}`}>
                                <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono">Question {displayIdx + 1} of {questions.length}</div>
                                <div className="mt-3 font-heading text-lg text-ink-900">{q.prompt}</div>
                                <div className="mt-5 space-y-2">
                                    {q.options.map((opt, oi) => {
                                        const selected = answers[displayIdx] === oi;
                                        return (
                                            <button
                                                key={oi}
                                                onClick={() => setAnswer(displayIdx, oi)}
                                                data-testid={`question-${q.id}-option-${oi}`}
                                                className={`w-full text-left px-4 py-3 border rounded-sm transition-colors text-sm ${selected ? "border-haevn-700 bg-haevn-50 text-ink-900" : "border-ink-300 hover:border-haevn-700 hover:bg-ink-100/60"}`}
                                            >
                                                <span className="font-mono text-xs text-ink-400 mr-3">{String.fromCharCode(65+oi)}</span>
                                                {opt}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}

                    <div className="flex items-center justify-between pt-4">
                        <div className="text-xs font-mono uppercase tracking-[0.15em] text-ink-500">
                            {answers.filter((a) => a >= 0).length} of {questions.length} answered
                        </div>
                        <button
                            onClick={submit}
                            disabled={busy || !allAnswered}
                            data-testid="quiz-submit-btn"
                            className="bg-haevn-700 text-white px-6 py-2.5 rounded-sm text-sm hover:bg-haevn-900 transition-colors disabled:opacity-50"
                        >
                            {busy ? "Submitting…" : "Submit"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function Result({ quiz, questions, order, submitted, retake, navigate }) {
    const { attempt, detail } = submitted;
    const detailById = new Map(detail.map((d) => [d.question_id, d]));
    return (
        <div className="space-y-8" data-testid="quiz-result">
            <div className={`p-8 border ${attempt.passed ? "bg-haevn-50 border-haevn-300" : "bg-clay-50 border-clay-50"}`}>
                <div className="text-[10px] uppercase tracking-[0.25em] font-mono text-ink-500">Result</div>
                <div className={`mt-2 font-editorial text-4xl ${attempt.passed ? "text-haevn-700" : "text-clay-500"}`}>
                    {attempt.passed ? "Passed." : "Not yet."}
                </div>
                <div className="mt-2 text-base text-ink-700">
                    Score <span className="font-mono">{attempt.score}%</span> · {attempt.correct} of {attempt.total} correct
                </div>
                {submitted.partner_next_url && attempt.passed && (
                    <div className="mt-6 p-5 border border-haevn-700 bg-white" data-testid="partner-cert-success">
                        <div className="text-[10px] uppercase tracking-[0.25em] font-mono text-haevn-700">Certified</div>
                        <div className="mt-1.5 font-editorial text-2xl text-ink-900">You&apos;re a certified Community Partner.</div>
                        <p className="mt-2 text-sm text-ink-500">
                            Your share link and QR code are waiting on your haevn.co dashboard. We&apos;ve also emailed them to you.
                        </p>
                        <a
                            href={submitted.partner_next_url}
                            data-testid="partner-dashboard-link"
                            className="mt-4 inline-flex items-center gap-2 bg-haevn-700 text-white px-5 py-2.5 rounded-sm text-sm hover:bg-haevn-900"
                        >
                            Continue to your haevn.co dashboard →
                        </a>
                    </div>
                )}
                <div className="mt-6 flex flex-wrap gap-3">
                    <button onClick={retake} data-testid="quiz-retake-btn" className="bg-haevn-700 text-white px-5 py-2 rounded-sm text-sm hover:bg-haevn-900">Retake</button>
                    <button onClick={() => navigate("/quizzes")} data-testid="quiz-back-list-btn" className="border border-ink-300 px-5 py-2 rounded-sm text-sm hover:border-haevn-700">Back to quizzes</button>
                </div>
            </div>
            <div className="space-y-4">
                {order.map((origIdx, displayIdx) => {
                    const q = questions[origIdx];
                    const d = detailById.get(q.id);
                    return (
                        <div key={q.id} className="bg-white border border-ink-300 p-6" data-testid={`result-question-${q.id}`}>
                            <div className="flex items-start gap-3">
                                <div className={`mt-1 w-5 h-5 flex items-center justify-center rounded-full text-white ${d?.correct ? "bg-haevn-700" : "bg-clay-500"}`}>
                                    {d?.correct ? <Check size={12} /> : <X size={12} />}
                                </div>
                                <div className="flex-1">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-ink-400 font-mono">Question {displayIdx + 1}</div>
                                    <div className="font-heading text-base mt-1">{q.prompt}</div>
                                    <div className="mt-3 text-sm">
                                        <div className="text-ink-500">Your answer: <span className="text-ink-900">{q.options[d?.answer] ?? "—"}</span></div>
                                        {!d?.correct && <div className="text-ink-500 mt-1">Correct: <span className="text-haevn-700">{q.options[d?.correct_index]}</span></div>}
                                        {d?.explanation && <div className="mt-2 text-ink-700 italic">{d.explanation}</div>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
